/**
 * Stories Domain Types
 *
 * Standalone types — no GraphQL dependency.
 */

// ============================================================================
// Re-export from standalone type files
// ============================================================================

/**
 * Enum types
 */
export {
  Language,
  AgeGroup,
  StoryType,
  StoryStatus,
  BlockStatus,
  CharacterRole,
  CharacterGender,
  CharacterCreationMethod,
  CharacterImportance,
  VoiceGender,
  StoryJobStatus,
  StoryJobType,
} from './enums';

/**
 * Core entity types
 */
export type {
  Story,
  StoryLegacyAudioFields,
  TextBlock,
  Character,
  CharacterAssignment,
  StoryJob,
  FamilyMemberRef,
  FamilyRef,
  StoryCharacterMapping,
  CharacterAppearanceRecord,
} from './story';


// ============================================================================
// Backward Compatibility Aliases
// ============================================================================

/**
 * @deprecated Use AgeGroup enum from GraphQL instead
 * Legacy string union type for age groups
 */
export type AgeGroupLegacy = '3-5' | '5-7' | '7-10' | '10+';

/**
 * @deprecated Use Language enum from GraphQL instead
 * Legacy string union type for languages
 */
export type LanguageLegacy = 'ru' | 'en' | 'cs' | 'uk' | 'de' | 'fr';

/**
 * @deprecated Use StoryType enum from GraphQL instead
 * Legacy string union type for story types
 */
export type StoryTypeLegacy = 'funny' | 'serious' | 'adventure' | 'bedtime';

/**
 * @deprecated Use BlockStatus enum from GraphQL instead
 * Legacy string union for block status
 */
export type BlockStatusLegacy = 'original' | 'edited' | 'regenerated';

/**
 * @deprecated Use CharacterRole enum from GraphQL instead
 * Legacy string union for character role
 */
export type CharacterRoleLegacy = 'hero' | 'friend' | 'helper' | 'obstacle';

/**
 * @deprecated Use CharacterGender enum from GraphQL instead
 * Legacy string union for character gender
 */
export type CharacterGenderLegacy = 'male' | 'female' | 'neutral';

/**
 * @deprecated Use CharacterCreationMethod enum from GraphQL instead
 * Legacy string union for character creation method
 */
export type CharacterCreationMethodLegacy = 'manual' | 'ai_generated' | 'ai_enhanced';

// ============================================================================
// Voice Types (pending GraphQL schema update in DAT-4)
// ============================================================================

// Re-exported from shared/types/audio.ts to break circular dep with audio-stories

// ============================================================================
// Domain-Specific Types (NOT in GraphQL)
// ============================================================================

/**
 * Enhancement level for prompt processing
 */
export type EnhancementLevel = 'light' | 'rich';

/**
 * Story Preview - lightweight version for lists
 */
export interface StoryPreview {
  id: string;
  title: string;
  status: string;
  createdAt: string;
  createdBy: string;
  blockCount: number;
  audioStatus?: string;
}

/**
 * Story creation method (internal tracking)
 */
export type StoryCreationMethod = 'ai_generated' | 'manual' | 'quick-generation';

// ============================================================================
// Quick Generation Types
// ============================================================================

export interface QuickGenSettings {
  targetAge: string;
  desiredDurationMinutes: number;  // 3-10 minutes
  selectedVoiceId: string;
}

// ============================================================================
// Prompt Enhancement Types
// ============================================================================

export interface PromptEnhancementResult {
  originalPrompt: string;
  enhancedPrompt: string;
  enhancementLevel: EnhancementLevel;
  tokensUsed: number;
  changes: string[];
}

// ============================================================================
// API Request/Response Types
// ============================================================================

export interface CreateStoryRequest {
  prompt: string;
  memberId?: string;        // Optional: can create story without linking to member
  ageGroup?: string;        // Optional: AI determines complexity
  voiceSettings?: import('@/shared/types/audio').TTSConfig;
  title?: string;
  language?: string;
  characterIds?: string[];
  desiredDuration?: number;
}

export interface CreateStoryResponse {
  storyId: string;
  jobId?: string;
  status: string;
  message: string;
}

export interface UpdateBlockRequest {
  text: string;
}

export interface RegenerateBlockRequest {
  context?: string;
}

export interface ListStoriesQuery {
  familyId?: string;
  memberId?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

export interface ListStoriesResponse {
  stories: import('./story').Story[];
  total: number;
  hasMore: boolean;
}

// ============================================================================
// LLM Generation Types
// ============================================================================

export interface LLMStoryResponse {
  title: string;
  blocks: Array<{
    order: number;
    text: string;
  }>;
}

export interface StoryValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
  stats: {
    blockCount: number;
    totalWords: number;
    avgWordsPerBlock: number;
    blocksOutOfRange: number;
  };
}

// ============================================================================
// Token Economy Types
// ============================================================================

export interface TokenCharge {
  operation: string;  // 'story_generation', 'audio_generation', etc.
  tokensUsed: number;
  timestamp: string;
  blockReference?: string;
}
